/**
 * The relay tunnel (M3 plan R10, spec §4 and §10): an open cylinder around the camera whose inside faces carry
 * scrolling TSL streaks and a travelling pulse. It fades in over `enter`, holds through `tunnel` (nearly opaque,
 * so the system switch behind it stays hidden) and fades out over `exit`. The cylinder is centred on the render
 * origin (the camera, D-011) with its axis along local Z; the caller turns `root` so local −Z is the flight
 * direction. The ship sits well inside the radius, so the chase view keeps it in front of the wall.
 *
 * `setPhase` and `update` run per frame and allocate nothing (D-014).
 */
import { BackSide, CylinderGeometry, Group, Mesh, MeshBasicNodeMaterial } from 'three/webgpu'
import { abs, float, floor, fract, hash, mix, sin, smoothstep, uniform, uv } from 'three/tsl'
import { linearRgb, setLinearRgb } from './color'
import type { TunnelConfig } from './config'

export type TunnelPhase = 'none' | 'enter' | 'tunnel' | 'exit'

/** The time uniform wraps here (s) so float32 keeps its precision in long sessions. */
const TIME_WRAP_S = 3600

export interface Tunnel {
  /** Place at the render origin; local −Z = the flight direction. Hidden while the opacity is 0. */
  readonly root: Group
  readonly mesh: Mesh
  /** The wall opacity the last `setPhase` wrote. */
  readonly opacity: number
  /** `t01` is the progress through `enter` or `exit` (ignored for `tunnel` and `none`). */
  setPhase(phase: TunnelPhase, t01: number): void
  /** Advances the streak scroll and the pulse. */
  update(dt: number): void
  /** Re-reads the config into the uniforms (dev-panel tuning). */
  refresh(): void
  dispose(): void
}

function clamp01(v: number): number {
  if (!(v > 0)) return 0
  return v < 1 ? v : 1
}

/** Smoothstep 0..1 of a clamped `t`. */
export function ease01(t: number): number {
  const c = clamp01(t)
  return c * c * (3 - 2 * c)
}

/** The wall opacity for a phase: an eased rise over `enter`, `maxOpacity` through `tunnel`, an eased fall over `exit`. */
export function tunnelOpacity(phase: TunnelPhase, t01: number, maxOpacity: number): number {
  if (phase === 'enter') return ease01(t01) * maxOpacity
  if (phase === 'tunnel') return maxOpacity
  if (phase === 'exit') return (1 - ease01(t01)) * maxOpacity
  return 0
}

export function createTunnel(cfg: TunnelConfig): Tunnel {
  const uTime = uniform(0)
  const uOpacity = uniform(0)
  const uWall = uniform(linearRgb(cfg.wallColor))
  const uStreak = uniform(linearRgb(cfg.streakColor))
  const uStreakIntensity = uniform(cfg.streakIntensity)
  const uColumns = uniform(cfg.streakColumns)
  const uRepeat = uniform(cfg.streakRepeat)
  const uScroll = uniform(cfg.streakScroll)
  const uWidth = uniform(cfg.streakWidth)
  const uDensity = uniform(cfg.streakDensity)
  const uPulseHz = uniform(cfg.pulseHz)
  const uPulse = uniform(cfg.pulseIntensity)
  const uEndFade = uniform(cfg.endFade)

  // CylinderGeometry runs along +Y with uv.y = 1 at the top; turned so the axis is Z, uv.y = 1 lies behind the camera.
  const geometry = new CylinderGeometry(cfg.radiusM, cfg.radiusM, cfg.lengthM, cfg.radialSegments, 1, true)
  geometry.rotateX(Math.PI / 2)

  const u = uv()
  const column = floor(u.x.mul(uColumns))
  const columnRand = hash(column)
  // Each column scrolls at its own rate towards +Z (past the camera, backwards).
  const along = u.y.mul(uRepeat).sub(uTime.mul(uScroll).mul(columnRand.mul(0.8).add(0.6))).add(columnRand.mul(7))
  const cell = floor(along) // hash() takes the seed as a uint: keep it positive (offset below)
  const present = smoothstep(uDensity.oneMinus(), uDensity.oneMinus().add(0.02), hash(cell.add(column.mul(131)).add(100000)))
  const across = abs(fract(u.x.mul(uColumns)).sub(0.5)).mul(2)
  const line = smoothstep(float(0), uWidth, across).oneMinus()
  const head = fract(along)
  const streak = line.mul(present).mul(head.mul(head))
  const pulse = smoothstep(0.9, 1, fract(u.y.mul(2).sub(uTime.mul(uPulseHz)))).mul(uPulse)
  const ends = smoothstep(0, uEndFade, u.y).mul(smoothstep(0, uEndFade, u.y.oneMinus()))
  const shimmer = sin(uTime.mul(2.1).add(u.x.mul(6.2831853))).mul(0.15).add(0.85)

  const material = new MeshBasicNodeMaterial({ side: BackSide, transparent: true, depthWrite: false })
  material.name = 'reachFx:tunnel'
  const wall = uWall
  material.colorNode = mix(wall, wall.add(uStreak.mul(streak.mul(uStreakIntensity).add(pulse))), ends).mul(shimmer)
  material.opacityNode = uOpacity

  const mesh = new Mesh(geometry, material)
  mesh.name = 'reachFx:tunnel'
  mesh.frustumCulled = false
  mesh.renderOrder = 10
  const root = new Group()
  root.name = 'reachFx:tunnelRoot'
  root.add(mesh)
  root.visible = false

  let opacity = 0

  return {
    root,
    mesh,
    get opacity(): number {
      return opacity
    },
    setPhase(phase: TunnelPhase, t01: number): void {
      opacity = tunnelOpacity(phase, t01, cfg.maxOpacity)
      uOpacity.value = opacity
      root.visible = opacity > 0
    },
    update(dt: number): void {
      const t = uTime.value + (dt > 0 ? dt : 0)
      uTime.value = t < TIME_WRAP_S ? t : t - TIME_WRAP_S
    },
    refresh(): void {
      setLinearRgb(uWall.value, cfg.wallColor)
      setLinearRgb(uStreak.value, cfg.streakColor)
      uStreakIntensity.value = cfg.streakIntensity
      uColumns.value = cfg.streakColumns
      uRepeat.value = cfg.streakRepeat
      uScroll.value = cfg.streakScroll
      uWidth.value = cfg.streakWidth
      uDensity.value = cfg.streakDensity
      uPulseHz.value = cfg.pulseHz
      uPulse.value = cfg.pulseIntensity
      uEndFade.value = cfg.endFade
    },
    dispose(): void {
      root.removeFromParent()
      geometry.dispose()
      material.dispose()
    },
  }
}
